import { AnimatedMaterialOptions } from "@/types";
import { MeshPhysicalMaterial, Color, TextureLoader, RepeatWrapping, MeshPhysicalMaterialParameters, LinearMipmapLinearFilter, LinearFilter } from "three";

export async function ZellwandMaterial(args: any) {
	const textureLoader = new TextureLoader();

	const clearcoatMap = await textureLoader.loadAsync("/static/textures/clearcoat.jpg");
	const normalMap = await textureLoader.loadAsync("/static/textures/voronoi_normal.jpg");

	clearcoatMap.repeat.set(2, 2);
	clearcoatMap.wrapS = clearcoatMap.wrapT = RepeatWrapping;
	clearcoatMap.minFilter = LinearMipmapLinearFilter;
	clearcoatMap.magFilter = LinearFilter;
	clearcoatMap.generateMipmaps = true;

	normalMap.repeat.set(3, 3);
	normalMap.wrapS = normalMap.wrapT = RepeatWrapping;

	normalMap.minFilter = LinearMipmapLinearFilter;
	normalMap.magFilter = LinearFilter;
	normalMap.generateMipmaps = true;

	const material = new MeshPhysicalMaterial(
		Object.assign(
			{
				transmission: 1.035,
				thickness: -0.5,
				roughness: 0.49,
				roughnessMap: clearcoatMap,
				ior: 1.6,
				// iridescence: 0.1,
				iridescenceIOR: 1.65,
				// iridescenceThicknessRange: [0, 1800],
				reflectivity: 1.6,
				dispersion: 0.5,
				attenuationDistance: 0.5,
				clearcoat: 1,
				clearcoatRoughness: 0.1,
				bumpMap: normalMap,

				clearcoatNormalMap: normalMap,
				clearcoatRoughnessMap: clearcoatMap,
				envMapIntensity: 1.0,
				specularIntensity: 0.1,
				// sheen: 1,
				// sheenRoughness: 0.71,

				sheen: 1.0, // Aktiviere Sheen-Effekt (Wert zwischen 0 und 1)
				sheenColor: new Color(0xffffff), // Sheen-Farbe (z.B. Rot)
				sheenRoughness: 0.8,
				sheenColorMap: clearcoatMap,
			},
			args
		) as MeshPhysicalMaterialParameters
	);

	// Object.assign(material, args);

	const uniforms = {
		time: { value: 0 },
	};

	// Vertex Shader für Noise-Verformung erweitern
	material.onBeforeCompile = (shader) => {
		shader.uniforms.time = uniforms.time;

		// Noise-Funktion in den Shader einbauen
		shader.vertexShader = shader.vertexShader.replace(
			`void main() {`,
			`
		float random(vec2 st) {
			return fract(sin(dot(st.xy, vec2(12.9898, 78.233))) * 43758.5453123);
		}

    
    
    float noise(vec2 st) {
        vec2 i = floor(st);
        vec2 f = fract(st);
        float a = random(i);
        float b = random(i + vec2(1.0, 0.0));
        float c = random(i + vec2(0.0, 1.0));
        float d = random(i + vec2(1.0, 1.0));
        vec2 u = f * f * (3.0 - 2.0 * f);
        return mix(a, b, u.x) + (c - a) * u.y * (1.0 - u.x) + (d - b) * u.x * u.y;
    }


    // Smooth Voronoi Noise
    float smoothVoronoi(vec2 st, float time) {
        vec2 p = floor(st);
        vec2 f = fract(st);

        float minDist = 10.0; // Initialer Abstand
        for (int y = -1; y <= 1; y++) {
            for (int x = -1; x <= 1; x++) {
                vec2 neighbor = vec2(float(x), float(y));
                vec2 point = vec2(
                    random(p + neighbor), 
                    random(p + neighbor + vec2(1.0, 1.0))
                ) + 0.5 * sin(time + dot(neighbor, vec2(12.9898, 78.233)));
                vec2 diff = neighbor + point - f;
                float dist = length(diff);
                minDist = min(minDist, dist);
            }
        }

        return 1.0 - minDist; // Invertiere für glatte Voronoi-Struktur
    }

		uniform float time;
		void main() {
		`
		);

		// Nach der Skinning-Berechnung modifizieren
		shader.vertexShader = shader.vertexShader.replace(
			`#include <skinning_vertex>`,
			`
      #include <skinning_vertex>
      
      // Modifiziere die skinned Position mit Noise
      float deformation = 20.0 * noise(transformed.xy + vec2(time)); 
      transformed += normal * deformation;
		`
		);
	};
	material.userData.animated = (opt: AnimatedMaterialOptions) => {
		const delta = opt.clock.getDelta();
		uniforms.time.value += delta;

		const multiplier = 0.05;

		normalMap.offset.setX(normalMap.offset.x + delta * multiplier);
		normalMap.offset.setY(normalMap.offset.y + delta * multiplier);
		clearcoatMap.offset.setX(clearcoatMap.offset.x + delta * multiplier);
		clearcoatMap.offset.setY(clearcoatMap.offset.y + delta * multiplier);
	};

	material.userData.cleanup = () => {
		clearcoatMap.dispose();
		normalMap.dispose();
	};

	return material;
}
